import CresAxios from "../../util/CresAxios";
import {ChartSpec} from "./chartSpec";

import {ReportQuery} from "./querySpec";
import {ResultColumn} from "./reportApi";

/**
 * Saved reports, and the evidence of having run one.
 *
 * SEPARATE FROM reportApi, which is the live surface -- what you may report on, and running
 * something now. This is the durable half: definitions somebody keeps, and runs kept as evidence.
 *
 * THERE IS NO UPDATE OR DELETE FOR A RUN HERE, and that is not an omission. A run is what a report
 * SAID at a moment; an inspector asking what it said in March needs an answer nobody could have
 * tidied. The server says the same thing twice more -- the database grant is SELECT and INSERT
 * only, and the component is noUpdate and noDelete -- so this is the third statement of one rule.
 */

const URL = "t10/v1/report_store";

export type SavedReport = {
    USID: string;
    Name: string;
    Description?: string | null;
    Dataset?: string | null;
    Specification: ReportQuery;
    /** How it is drawn. Absent until somebody builds a chart; the client then falls back to the
     *  default for the result, which is what this always drew. Presentation, never compiled. */
    ChartSpecification?: ChartSpec | null;
    LastRun?: string | null;
    LastRunRowCount?: number | null;
    RunCount?: number;
};

export type ReportRun = {
    USID: string;
    ReportUSID?: string | null;
    RunDatetime: string;
    RowCount: number;
    Truncated: boolean;
};

export type StoredRun = ReportRun & {
    Specification: ReportQuery;
    Columns: ResultColumn[];
    Rows: unknown[][];
};

export function getSavedReports(): Promise<SavedReport[]> {
    return CresAxios({method: "GET", url: URL})
        .then((response: any) => response.data?.Reports ?? []);
}

/** Save a report. OMITTING ChartSpecification leaves any stored chart alone -- the server treats
 *  absent as "say nothing about it", because most saves are about the name or the shelves. */
export function saveReport(report: {USID?: string; Name: string; Description?: string;
                                    Specification: ReportQuery;
                                    ChartSpecification?: ChartSpec | null}): Promise<{USID: string}> {
    return CresAxios({method: "PUT", url: URL, data: report})
        .then((response: any) => response.data);
}

export type ExportFormat = "xlsx" | "parquet" | "avro";

export type ExportedRun = {
    FileName: string;
    ContentType: string;
    /** A short-lived presigned S3 URL. The FILE never comes through the API -- see resultExcel. */
    Url: string;
    /** Seconds the link is good for, said rather than left for the page to assume. */
    ExpiresIn: number;
    RowCount: number;
    Truncated: boolean;
};

export function deleteReport(usid: string): Promise<void> {
    return CresAxios({method: "DELETE", url: URL, params: {USID: usid}}).then(() => undefined);
}

/**
 * Run a report AND keep what it said.
 *
 * The plain run in reportApi answers the screen and keeps nothing, which is right while somebody is
 * still arranging shelves. This one is for the run that matters -- and it records the run whether
 * or not the report has been saved, because running it is how somebody arrives at one worth
 * keeping, and those rows are evidence of what the data said at that moment either way.
 */
export function runAndRecord(query: ReportQuery, reportUSID?: string | null) {
    return CresAxios({method: "PUT", url: URL, data: {Query: query, ReportUSID: reportUSID}})
        .then((response: any) => response.data);
}

export function getRuns(reportUSID: string): Promise<ReportRun[]> {
    return CresAxios({method: "GET", url: URL, params: {ReportUSID: reportUSID}})
        .then((response: any) => response.data?.Runs ?? []);
}

export function getRun(usid: string): Promise<StoredRun> {
    return CresAxios({method: "GET", url: URL, params: {Runs: true, USID: usid}})
        .then((response: any) => response.data?.Run);
}

/**
 * A recorded run as a spreadsheet.
 *
 * ON THE SERVER, unlike the CSV beside it, and the reasons are specific rather than a preference:
 *
 *   THE BYTES ARE THE RECORDED ROWS, not the grid's buffer -- the same reason the chart reads from
 *   the recorded run. A file somebody attaches to a submission and the evidence the store kept are
 *   then the same numbers by construction.
 *
 *   IT IS NOT A SECOND PLACE SERVING TENANT ROWS. `Export Report Run` reads through
 *   `Get Report Runs`, which already refuses a run produced under another persona, so the rule that
 *   protects those rows has one implementation rather than two that must agree.
 *
 *   AND NO SPREADSHEET LIBRARY SHIPS TO THE BROWSER for it. openpyxl is already in the Lambda image.
 *
 * Base64 in JSON rather than a binary body, so nothing at the API Gateway edge has to be taught
 * which media types are binary for a file of at most a thousand rows.
 */
export function exportRun(usid: string, format: ExportFormat = "xlsx"): Promise<ExportedRun> {
    return CresAxios({method: "GET", url: URL, params: {Export: format, USID: usid}})
        .then((response: any) => response.data);
}
